import ApiInstance from "./api-config";
import axios from "axios";
import {useApiErrorResponseHandler} from "../utils/ApiResponseHandler";

/** Loan Product
 *  @typedef {Object} LoanProduct
 *  @property {number}          id                             * Identifier                   *
 *  @property {number}          name                           * Name                         *
 *  @property {(null | any)}    icon                           * Icon                         *
 *  @property {(null | string)} description                    * Description                  *
 *  @property {string}          release_date                   * Release Date                 *
 *  @property {number}          auto_payment                   * Auto Payment                 *
 *  @property {string}          loan_duration_period           * Loan Duration Period         *
 *  @property {string}          loan_interest_period           * Loan Interest Period         *
 *  @property {string}          min_loan_duration              * Minimum Loan Duration        *
 *  @property {string}          def_loan_duration              * Default Loan Duration        *
 *  @property {string}          max_loan_duration              * Maximum Loan Duration        *
 *  @property {string}          min_principal_amount           * Minimum Principal Amount     *
 *  @property {string}          def_principal_amount           * Default Principal Amount     *
 *  @property {string}          max_principal_amount           * Maximum Principal Amount     *
 *  @property {string}          min_loan_interest              * Minimum Loan Interest        *
 *  @property {string}          def_loan_interest              * Default Loan Interest        *
 *  @property {string}          max_loan_interest              * Maximum Loan Interest        *
 *  @property {string}          min_num_of_repayments          * Minimum Number of Repayments *
 *  @property {string}          def_num_of_repayments          * Default Number of Repayments *
 *  @property {string}          max_num_of_repayments          * Maximum Number of Repayments *
 *  @property {(null | any)}    tag                            * Tag                          *
 *  @property {number}          status                         * Status                       *
 *  @property {string}          created_at                     * Date of Creation             *
 *  @property {string}          updated_at                     * Date of Update               *
 *  @property {(null | string)} icon_alt                       * Alternate Icon               *
 *  @property {number}          wiz_steps                      * Wiz Steps                    *
 *  @property {string}          loan_type                      * Loan Type                    *
 *  @property {number}          loan_child_type_id             * Loan Child Type ID           *
 *  @property {number}          is_digitize                    * Digitized?                   *
 */
/** Loan Child Type
 *  @typedef {Object} LoanChildType
 *  @property {number}             id                         * Identifier               *
 *  @property {string}             name                       * Name                     *
 *  @property {string}             type_name                  * Name of Type             *
 *  @property {(null | string)}    description                * Description              *
 *  @property {string}             loan_type_id               * Loan Type ID             *
 *  @property {string}             created_at                 * Date of Creation         *
 *  @property {number}             updated_at                 * Date of Update           *
 *  @property {Array<LoanProduct>} [loan_products=[]]         * Loan Products (Optional) *
 */
/** Loan Product Type
 *  @typedef {Object} LoanProductType
 *  @property {string}                name                     * Name                     *
 *  @property {(null | string)}      type_name                * Type Name                *
 *  @property {(null | string)}      description              * Description              *
 *  @property {string}               created_at               * Date of Creation         *
 *  @property {number}               updated_at               * Date of Update           *
 *  @property {(null | any)}         icon                     * Icon                     *
 *  @property {Array<LoanChildType>} loan_child_type          * Type of Child Loan       *
 */
/**
 *  Retrieve/Read All Loan-Products-List Endpoint
 *  @description                                                        - [API] Fetches loan products from the Loan Management
 *                                                                        System.
 *  @author                                                             - Dowc Jr.
 *  @returns  {
 *    (Promise<Array<LoanProductType>> | Promise<string> | Promise<null>)
 *  }                                                                   - List of loan products available on the LMS
 *                                                                        platform.
 *  @throws {Error}                                                     - Rejects if fetch request endpoint for loan
 *                                                                        products fails.
 *  @example
 *  getLoans().then((loanProducts) => {
 *      const totalLoans = loanProducts.length;
 *      console.log("Total Loans: ", totalLoans);
 *  });
 */
const getLoans = async () => {
    let loansPayload = null;
    try {
        const {status, data} = await ApiInstance.get("/get-loan-products");
        if (status === 200) {
            loansPayload = data;
        }
    } catch (getLoansEndpointError) {
        if (getLoansEndpointError instanceof Error) {
            const errorMessage = getLoansEndpointError.message;
            if (errorMessage === 'Network Error') {
                loansPayload = `EXTERNAL APPLICATION FAILURE. Failed to establish network connection.`;
            } else {
                loansPayload = `INTERNAL APPLICATION FAILURE. ${errorMessage}.`;
            }
        }
        if (axios.isAxiosError(getLoansEndpointError)) {
            if (getLoansEndpointError.response) {
                loansPayload = useApiErrorResponseHandler(getLoansEndpointError.code);
            } else if (getLoansEndpointError.request) {
                loansPayload = useApiErrorResponseHandler(getLoansEndpointError.code);
            } else {
                loansPayload = getLoansEndpointError.message;
            }
        }
    }
    return loansPayload;
}
/*xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx*/
/**********************************************************************************************************************/
/**
 *  @typedef {Object} LoanProductPayload
 *  @property {string} fname                * First Name       *
 *  @property {string} lname                * Last Name        *
 *  @property {string} email                * Email Address    *
 *  @property {string} mobile               * Contact Number   *
 *  @property {number} amount               * Principal Amount *
 *  @property {number} duration             * Payment Duration *
 *  @property {(null | number)} loanType    * Type of Loan     *
 */
/**
 *  Create Loan-Product-Application Endpoint
 *  @description                                               - [API] Submit a loan application to the Loan Management System.
 *  @param  {LoanProductPayload} loanPayload                   - Loan product data payload.
 *  @author                                                    - Dowc Jr.
 *  @returns  {
 *    (Promise<boolean> | Promise<string> | Promise<null>)
 *  }                                                          - Otherwise true if the loan submission is successful.
 *  @throws {Error}                                            - Reject request if loan product application submission fails.
 *  @example
 *  createLoan(loanDetails).then((result) => {
 *      if (result) {
 *          console.log("Loan submission successful");
 *      } else {
 *          const errorMessage = result;
 *      }
 *  });
 */
const createLoan = async (loanPayload) => {
    let loanApplicationResult = null;
    try {
        const {status, code} = await ApiInstance.post("/apply-loan", loanPayload);
        console.log("status result",status);
        if (status === 200) {
            loanApplicationResult = true;
            return loanApplicationResult;
        } else {
            loanApplicationResult = `INTERNAL APPLICATION FAILURE.
                                     Unknown application error.`;
        }
    } catch (createLoanError) {
        if (createLoanError instanceof Error) {
            const errorMessage = createLoanError.message;
            if (errorMessage === 'Network Error') {
                loanApplicationResult = `EXTERNAL APPLICATION FAILURE. Failed to establish network connection.`;
            } else {
                loanApplicationResult = `INTERNAL APPLICATION FAILURE. ${errorMessage}.`;
            }
        }
        if (axios.isAxiosError(createLoanError)) {
            if (createLoanError.response) {
                loanApplicationResult = useApiErrorResponseHandler(createLoanError.code);
            } else if (createLoanError.request) {
                loanApplicationResult = useApiErrorResponseHandler(createLoanError.code);
            } else {
                loanApplicationResult = createLoanError.message;
            }
        }
        return loanApplicationResult;
    }
}
/*xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx*/

const LoanApiService = {
    getLoans,
    createLoan
};

export default LoanApiService;

